import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Alert,
  CircularProgress,
  MenuItem,
  Menu,
  FormControlLabel,
  Switch,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  Chip,
  InputAdornment,
  Select,
  FormControl,
  FormHelperText,
  InputLabel,
  Slider,
  Checkbox,
  Radio,
  RadioGroup,
  Tooltip,
} from '@mui/material';
import {
  PencilSimple,
  Trash,
  DotsThreeVertical,
  Plus,
  ArrowSquareOut,
  Copy,
  X,
  DotsSixVertical,
  Eye,
  Table as TableIcon,
  PaperPlaneTilt,
  MagnifyingGlass,
  CaretLeft,
  ArrowLeft,
  Gear,
  Info,
} from '@phosphor-icons/react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api from '../../utils/api';
import LeadCoupleAutocomplete from '../Common/LeadCoupleAutocomplete';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useBusiness } from '../../context/BusinessContext';
import useThemeStyles from '../../theme/useThemeStyles';
import PortalSnackbar from '../Common/PortalSnackbar';
import FormSubmissionEntryDialog from './FormSubmissionEntryDialog';
import PotentialSpamChip, { isPotentialSpam, hasFailedDeferredStep } from './PotentialSpamChip';
import { DEFAULT_MAILCHIMP_CONSENT_MESSAGE } from '../../constants/mailchimpFormConsent';

import { Formik, Form, Field, FieldArray, useFormikContext } from 'formik';
import * as Yup from 'yup';

/** Ensures select/radio/checkbox fields have a usable options array in Formik (API may omit or use legacy shapes). */
function normalizeFieldOptions(field) {
  const raw = field.options;
  const typesNeeding = ['select', 'radio', 'checkbox'];
  if (!typesNeeding.includes(field.type)) {
    return Array.isArray(raw) ? raw : [];
  }
  if (!raw || !Array.isArray(raw) || raw.length === 0) {
    return [{ label: '', value: '' }];
  }
  return raw.map((o) => {
    if (typeof o === 'string') {
      return { label: o, value: o };
    }
    return {
      label: o?.label != null ? String(o.label) : '',
      value: o?.value != null ? String(o.value) : ''
    };
  });
}

const EMAIL_LIKE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Resolves Mongo id string from submission.lead / convertedLead (ObjectId or populated doc). */
function getCrmLeadIdFromEntry(entry) {
  if (!entry) return null;
  const c = entry.convertedLead;
  const l = entry.lead;
  if (c && typeof c === 'object' && c._id) return String(c._id);
  if (l && typeof l === 'object' && l._id) return String(l._id);
  if (c != null) return String(c);
  if (l != null) return String(l);
  return null;
}

/** Returns a usable email from submission data (matches API expectation: `email` preferred). */
function getSubmissionEmailForCrm(data) {
  if (!data || typeof data !== 'object') return '';
  const direct = data.email;
  if (direct != null && String(direct).trim()) {
    const e = String(direct).trim();
    if (EMAIL_LIKE.test(e)) return e;
  }
  for (const v of Object.values(data)) {
    if (typeof v === 'string' && EMAIL_LIKE.test(v.trim())) {
      return v.trim();
    }
  }
  return '';
}

function getSubmissionNameForDisplay(data) {
  if (!data || typeof data !== 'object') return '';
  const directName = data.name ?? data.Name ?? data.fullName ?? data.fullname;
  if (directName != null && String(directName).trim()) {
    return String(directName).trim();
  }
  const firstName = data.firstName ?? data.firstname;
  const lastName = data.lastName ?? data.lastname;
  const combined = `${firstName || ''} ${lastName || ''}`.trim();
  if (combined) return combined;
  return '';
}

/** Show "Add to couples" when the submission is not linked and includes a resolvable email. */
function canOfferAddPersonToCrm(entry) {
  if (!entry?._id) return false;
  if (entry.convertedLead) return false;
  const leadRef = entry.lead?._id || entry.lead;
  if (leadRef) return false;
  return Boolean(getSubmissionEmailForCrm(entry.data || {}));
}

/** Syncs Formik dirty state to parent for unsaved-changes navigation guard. */
function FormDirtySync({ onDirtyChange }) {
  const { dirty } = useFormikContext();
  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);
  return null;
}

const FormLeadsManagement = ({ formAction, formsView: formsViewProp }) => {
  const styles = useThemeStyles();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [clearingSpamId, setClearingSpamId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentFormId, setCurrentFormId] = useState(null);
  const [showEmbedCode, setShowEmbedCode] = useState(false);
  const [embedCode, setEmbedCode] = useState('');
  const [isCopied, setIsCopied] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFormId, setPreviewFormId] = useState(null);
  const [previewFormData, setPreviewFormData] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  /** Email lookup against existing couples when viewing an entry (not yet linked on the submission). */
  const [crmLeadLookup, setCrmLeadLookup] = useState({ status: 'idle', leadId: null });
  const [_defaultEntriesCount, setDefaultEntriesCount] = useState(0);
  const [_defaultConvertedCount, setDefaultConvertedCount] = useState(0);
  const [_defaultConversionPercent, setDefaultConversionPercent] = useState(0);
  const [_defaultEntriesLoading, setDefaultEntriesLoading] = useState(false);
  const [formSubmissionCounts, setFormSubmissionCounts] = useState({});
  const [searchQuery, _setSearchQuery] = useState('');
  const [sortBy, _setSortBy] = useState('name'); // 'name', 'response', 'lastModified', 'created'
  const [sortOrder, _setSortOrder] = useState('asc'); // 'asc', 'desc'
  const [allEntries, setAllEntries] = useState([]);
  const [allEntriesLoading, setAllEntriesLoading] = useState(false);
  const [allEntriesError, setAllEntriesError] = useState('');
  const [entrySearchQuery, setEntrySearchQuery] = useState('');
  const [selectedFormFilter, setSelectedFormFilter] = useState('all');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('all');
  const [entriesPage, setEntriesPage] = useState(1);
  const [entriesRowsPerPage, setEntriesRowsPerPage] = useState(20);
  const [entriesTotalCount, setEntriesTotalCount] = useState(0);
  const [selectedFieldIndex, setSelectedFieldIndex] = useState(null);
  /** After a failed save validation, show errors on preview and sidebar without requiring blur. */
  const [showFormValidationErrors, setShowFormValidationErrors] = useState(false);
  const [formEditorDirty, setFormEditorDirty] = useState(false);
  const [leaveFormDialogOpen, setLeaveFormDialogOpen] = useState(false);
  const [allowedDomainsSettingsOpen, setAllowedDomainsSettingsOpen] = useState(false);
  const getEmbedBaseUrl = () => {
    const raw = process.env.REACT_APP_API_URL;
    if (raw != null && String(raw).trim() !== '') {
      return String(raw).trim().replace(/\/+$/, '');
    }
    /* Match frontend/src/utils/api.js: embed script lives on API host, not the CRA dev server. */
    return 'http://localhost:5001';
  };
  const buildEmbedScriptTag = (targetId) => {
    const embedUrl = `${getEmbedBaseUrl()}/embed-form.js?formId=${targetId}`;
    return `<script src="${embedUrl}" data-form-id="${targetId}" data-cfasync="false" nowprocket data-no-optimize="1"></script>`;
  };
  const getDefaultAllowedHost = () => {
    try {
      const u = new URL(getEmbedBaseUrl());
      return u.hostname;
    } catch (e) {
      return 'localhost';
    }
  };

  /** Hostnames merged server-side; hidden from the allowed-domains text field. */
  const parseWebsiteHostname = (urlOrHost) => {
    if (!urlOrHost || typeof urlOrHost !== 'string') return '';
    const s = urlOrHost.trim();
    if (!s) return '';
    try {
      const u = new URL(s.includes('://') ? s : `https://${s}`);
      return u.hostname.toLowerCase();
    } catch (e) {
      return s.replace(/^https?:\/\//, '').replace(/\/.*$/, '').split(':')[0].toLowerCase();
    }
  };

  // Platform hosts are added on save. The business website stays visible, otherwise
  // typing it and saving looks like it was deleted on the next load.
  const getHiddenAllowedDomainHosts = () => new Set([
    getDefaultAllowedHost().toLowerCase(),
    'idotoo.ai',
    'www.idotoo.ai'
  ]);

  const filterDomainsForFormInput = (domainsArray) => {
    if (!domainsArray || !Array.isArray(domainsArray)) return '';
    const hidden = getHiddenAllowedDomainHosts();
    return domainsArray
      .map((d) => String(d).trim().toLowerCase())
      .filter((d) => d && !hidden.has(d))
      .join(', ');
  };
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [submissionToDelete, setSubmissionToDelete] = useState(null);
  const [deletingSubmission, setDeletingSubmission] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendFormTarget, setSendFormTarget] = useState(null);
  const [sendLeadId, setSendLeadId] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    fields: [],
    isActive: true,
    kind: 'website',
    emailNotification: {
      enabled: true,
      subject: 'New enquiry from your website',
      template: 'default',
      recipientEmail: ''
    },
    mailchimp: {
      enabled: false,
      consentMessage: ''
    }
  });
  const [searchParams] = useSearchParams();
  const location = useLocation();
  /** List view: `submissions` (default) or `builder` (from route or URL). */
  const formsView =
    formsViewProp ||
    (location.pathname.includes('/supplier/forms/builder') ? 'builder' : 'submissions');
  const isDefaultForm = formData?.isDefault;
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Compulsory fields for default form
  const compulsoryFieldNames = ['name', 'email', 'phone', 'weddingDate', 'message'];
  const isCompulsoryField = (fieldName) => isDefaultForm && compulsoryFieldNames.includes(fieldName);
  
  const navigate = useNavigate();

  const handleBackToFormsClick = () => {
    if (formEditorDirty) {
      setLeaveFormDialogOpen(true);
    } else {
      navigate('/supplier/forms/builder');
    }
  };

  const handleConfirmLeaveFormEditor = () => {
    setLeaveFormDialogOpen(false);
    navigate('/supplier/forms/builder');
  };

  const params = useParams();
  const formId = params.formId;
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.business?._id;
  const [mailchimpStatus, setMailchimpStatus] = useState({ connected: false, hasAudience: false });
  const scopedParams = (params = {}) => ({
    ...params,
    ...(businessId ? { businessId } : {})
  });

  useEffect(() => {
    if (!businessId) {
      setMailchimpStatus({ connected: false, hasAudience: false });
      return;
    }
    let cancelled = false;
    api.get('/api/mailchimp/status', { params: { businessId } })
      .then(({ data }) => {
        if (cancelled) return;
        setMailchimpStatus({
          connected: Boolean(data?.connected),
          hasAudience: Boolean(data?.hasAudience)
        });
      })
      .catch(() => {
        if (cancelled) return;
        setMailchimpStatus({ connected: false, hasAudience: false });
      });
    return () => {
      cancelled = true;
    };
  }, [businessId]);

  useEffect(() => {
    if (!selectedEntry || !businessId) {
      setCrmLeadLookup({ status: 'idle', leadId: null });
      return;
    }
    const email = getSubmissionEmailForCrm(selectedEntry.data || {});
    const linkedId = getCrmLeadIdFromEntry(selectedEntry);
    if (linkedId) {
      setCrmLeadLookup({ status: 'done', leadId: String(linkedId) });
      return;
    }
    if (!email) {
      setCrmLeadLookup({ status: 'done', leadId: null });
      return;
    }
    let cancelled = false;
    setCrmLeadLookup({ status: 'loading', leadId: null });
    api
      .get('/api/leads/lookup-email', { params: scopedParams({ email }) })
      .then(({ data }) => {
        if (cancelled) return;
        const lid = data.lead?._id ? String(data.lead._id) : null;
        setCrmLeadLookup({ status: 'done', leadId: lid });
      })
      .catch(() => {
        if (cancelled) return;
        setCrmLeadLookup({ status: 'done', leadId: null });
      });
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [selectedEntry, businessId]);
  
  useEffect(() => {
    if (!isEditMode) {
      setFormEditorDirty(false);
    }
  }, [isEditMode]);

  useEffect(() => {
    // If we're on the create or edit page, show form editor
    if (formAction === 'create') {
      setIsEditMode(true);
      const kindParam = searchParams.get('kind') === 'questionnaire' ? 'questionnaire' : 'website';
      setFormData({
        name: '',
        description: '',
        fields: [],
        isActive: true,
        kind: kindParam,
        emailNotification: {
          enabled: true,
          subject: 'New enquiry from your website',
          template: 'default',
          recipientEmail: ''
        },
        mailchimp: {
          enabled: false,
          consentMessage: ''
        }
      });
      setLoading(false);
    } else if (formAction === 'edit' && formId) {
      setIsEditMode(true);
      fetchFormById(formId);
    } else {
      setIsEditMode(false);
      // Normal form listing
      fetchForms();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [formAction, formId, selectedBusiness, searchParams]);

  /** Deep-link: ?formId= pre-selects the submissions filter when forms are loaded. */
  useEffect(() => {
    const fid = searchParams.get('formId');
    if (!fid || forms.length === 0) return;
    const exists = forms.some((f) => String(f._id) === String(fid));
    if (exists) setSelectedFormFilter(fid);
  }, [searchParams, forms]);

  useEffect(() => {
    setEntriesPage(1);
  }, [selectedFormFilter, selectedStatusFilter, entrySearchQuery]);

  /** Deep-link: ?formId= & submissionId= opens the submission entry modal (e.g. from email). */
  useEffect(() => {
    const sid = searchParams.get('submissionId');
    if (!sid || allEntriesLoading) return;
    const entry = allEntries.find((e) => String(e._id) === String(sid));
    if (!entry) return;
    const fid = searchParams.get('formId');
    if (fid && String(entry.formId) !== String(fid)) return;
    const formMatch = forms.find((f) => String(f._id) === String(entry.formId));
    setSelectedFormFilter(fid || entry.formId);
    setSelectedForm(formMatch || null);
    setSelectedEntry(entry);
    const next = new URLSearchParams(searchParams);
    next.delete('submissionId');
    navigate(
      `${location.pathname}${next.toString() ? `?${next.toString()}` : ''}`,
      { replace: true }
    );
  }, [searchParams, allEntries, allEntriesLoading, forms, navigate, location.pathname]);

  const fetchFormById = async (id) => {
    setLoading(true);
    try {
      const response = await api.get(`/api/forms/${id}`, { params: scopedParams() });
      setFormData({ ...response.data, kind: response.data.kind || 'website' });
    } catch (err) {
      setError('Failed to load form details');
    } finally {
      setLoading(false);
    }
  };

  const fetchForms = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/forms', { params: scopedParams() });
      setForms(response.data);
      const defaultForm = response.data.find(f => f.isDefault);
      if (defaultForm) {
        loadDefaultStats(defaultForm._id);
      } else {
        setDefaultEntriesCount(0);
        setDefaultConvertedCount(0);
        setDefaultConversionPercent(0);
      }
      // Fetch submission counts for all forms
      fetchSubmissionCounts(response.data);
      // Fetch submissions list after forms are loaded
      fetchAllEntries();
    } catch (err) {
      // Provide fallback data when API fails with 502
      if (err.response?.status === 502) {
        // Set empty forms array so UI doesn't break
        setForms([]);
        setError('Unable to connect to forms service. Please try again later.');
      } else {
        setError('Failed to load forms');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSubmissionCounts = async (formsList) => {
    const counts = {};
    try {
      await Promise.all(
        formsList.map(async (form) => {
          try {
            const { data } = await api.get(`/api/forms/${form._id}/submissions`, {
              params: scopedParams({ limit: 1 })
            });
            counts[form._id] = data.pagination?.total || 0;
          } catch (err) {
            counts[form._id] = 0;
          }
        })
      );
      setFormSubmissionCounts(counts);
    } catch (err) {}
  };

  const fetchAllEntries = async (override = {}) => {
    setAllEntriesLoading(true);
    setAllEntriesError('');
    try {
      const targetPage = override.page || entriesPage;
      const targetLimit = override.limit || entriesRowsPerPage;
      const response = await api.get('/api/forms/submissions', {
        params: scopedParams({
          page: targetPage,
          limit: targetLimit,
          formId: selectedFormFilter !== 'all' ? selectedFormFilter : undefined,
          status: selectedStatusFilter,
          search: entrySearchQuery || undefined
        })
      });
      const submissions = response.data?.submissions || [];
      const pagination = response.data?.pagination || {};
      setAllEntries(submissions);
      setEntriesTotalCount(pagination.total || submissions.length || 0);
      const safePage = pagination.page || targetPage || 1;
      if (safePage !== entriesPage) {
        setEntriesPage(safePage);
      }
    } catch (err) {
      setAllEntries([]);
      setEntriesTotalCount(0);
      setAllEntriesError('Failed to load entries');
    } finally {
      setAllEntriesLoading(false);
    }
  };
  useEffect(() => {
    if (isEditMode || formsView !== 'submissions') return;
    fetchAllEntries();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [entriesPage, entriesRowsPerPage, selectedFormFilter, selectedStatusFilter, entrySearchQuery, isEditMode, formsView, businessId]);



  const handleMenuOpen = (event, formId) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setCurrentFormId(formId);
    setSelectedForm(forms.find(f => f._id === formId) || null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setCurrentFormId(null);
  };

  const goCreateForm = (kind) => {
    navigate(`/supplier/forms/create?kind=${kind}`);
  };

  const openSendQuestionnaire = (form) => {
    setSendFormTarget(form);
    setSendLeadId('');
    setSendDialogOpen(true);
  };

  const confirmSendQuestionnaire = async () => {
    if (!sendFormTarget?._id || !sendLeadId) return;
    try {
      const { data } = await api.post(
        `/api/forms/${sendFormTarget._id}/questionnaire-sends`,
        { leadId: sendLeadId },
        { params: scopedParams() }
      );
      setSuccessMessage(data.message || 'Email sent to the couple.');
      setSendDialogOpen(false);
      setSendFormTarget(null);
      fetchForms();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send questionnaire');
    }
  };

  const copyQuestionnaireShareLink = async (formOrId) => {
    const id = formOrId?._id || formOrId;
    if (!id) {
      setError('Save the questionnaire first.');
      return;
    }
    try {
      const { data } = await api.post(
        `/api/forms/${id}/questionnaire-share-link`,
        {},
        { params: scopedParams() }
      );
      const url = data.shareUrl || '';
      if (url) {
        await navigator.clipboard.writeText(url);
      }
      setSuccessMessage(url ? 'Shareable link copied to clipboard.' : 'Link ready.');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not copy link. Save the questionnaire first.');
    }
  };

  const handleEditForm = () => {
    handleMenuClose();
    navigate(`/supplier/forms/edit/${currentFormId}`);
  };


  const handleDeleteForm = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  const confirmDeleteForm = async () => {
    try {
      await api.delete(`/api/forms/${currentFormId}`, { params: scopedParams() });
      const updatedForms = forms.filter(form => form._id !== currentFormId);
      setForms(updatedForms);
      // Remove count for deleted form
      setFormSubmissionCounts(prev => {
        const newCounts = { ...prev };
        delete newCounts[currentFormId];
        return newCounts;
      });
      setSuccessMessage('Form deleted successfully');
      setDeleteDialogOpen(false);
    } catch (err) {
      setError('Failed to delete form');
    }
  };

  const handleShowEmbedCode = () => {
    handleMenuClose();
    const form = forms.find(f => f._id === currentFormId);
    if (form?.kind === 'questionnaire') {
      setSuccessMessage('Questionnaires are not embedded. Use Copy shareable link or Send email to couple.');
      return;
    }
    if (form) {
      setEmbedCode(buildEmbedScriptTag(currentFormId));
      setShowEmbedCode(true);
    }
  };

  const _handleShowEmbedCodeWithForm = (form) => {
    if (!form?._id) return;
    setCurrentFormId(form._id);
    setSelectedForm(form);
    setEmbedCode(buildEmbedScriptTag(form._id));
    setShowEmbedCode(true);
  };

  const buildEmbedPreviewHtml = (targetId) => `
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Form Preview</title>
        <style>body { margin: 0; padding: 16px;     font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif; }</style>
      </head>
      <body>
        ${buildEmbedScriptTag(targetId)}
      </body>
    </html>
  `;

  const openFormPreview = async (formIdToUse) => {
    const targetId = formIdToUse || currentFormId || formId;
    if (!targetId) {
      setError('Please save the form before previewing.');
      return;
    }
    const listForm = forms.find((f) => String(f._id) === String(targetId));
    if (listForm?.kind === 'questionnaire' || formData?.kind === 'questionnaire') {
      try {
        const { data } = await api.post(
          `/api/forms/${targetId}/questionnaire-share-link`,
          {},
          { params: scopedParams() }
        );
        if (data.shareUrl) {
          window.open(data.shareUrl, '_blank', 'noopener,noreferrer');
        }
      } catch (e) {
        setError(e.response?.data?.message || 'Could not open preview. Save the questionnaire first.');
      }
      return;
    }
    setPreviewFormId(targetId);
    setPreviewFormData(buildEmbedPreviewHtml(targetId));
    setPreviewOpen(true);
  };

  const loadDefaultStats = async (formId) => {
    if (!formId) {
      setDefaultEntriesCount(0);
      setDefaultConvertedCount(0);
      setDefaultConversionPercent(0);
      return;
    }
    try {
      setDefaultEntriesLoading(true);
      const [{ data: totalData }, { data: convertedData }] = await Promise.all([
        api.get('/api/forms/submissions', {
          params: scopedParams({ formId, page: 1, limit: 1, status: 'all' })
        }),
        api.get('/api/forms/submissions', {
          params: scopedParams({ formId, page: 1, limit: 1, status: 'converted' })
        })
      ]);
      const total = totalData?.pagination?.total || 0;
      const converted = convertedData?.pagination?.total || 0;
      const percent = total > 0 ? Math.round((converted / total) * 100) : 0;
      setDefaultEntriesCount(total);
      setDefaultConvertedCount(converted);
      setDefaultConversionPercent(percent);
    } catch (err) {
      setDefaultEntriesCount(0);
      setDefaultConvertedCount(0);
      setDefaultConversionPercent(0);
    } finally {
      setDefaultEntriesLoading(false);
    }
  };

  const requestDeleteSubmission = (entry, event) => {
    event?.stopPropagation();
    if (!entry?._id) return;
    setSubmissionToDelete(entry);
  };

  const confirmDeleteSubmission = async () => {
    const entry = submissionToDelete;
    const formId = entry?.formId || selectedForm?._id;
    if (!entry?._id || !formId) return;
    try {
      setDeletingSubmission(true);
      setAllEntriesError('');
      await api.delete(`/api/forms/${formId}/submissions/${entry._id}`, { params: scopedParams() });
      setSuccessMessage('Submission deleted');
      setSubmissionToDelete(null);
      if (selectedEntry?._id === entry._id) {
        setSelectedEntry(null);
        setSelectedForm(null);
      }
      const nextPage = allEntries.length <= 1 && entriesPage > 1 ? entriesPage - 1 : entriesPage;
      fetchAllEntries({ page: nextPage });
      setFormSubmissionCounts((prev) => ({
        ...prev,
        [formId]: Math.max(0, (prev[formId] || 1) - 1)
      }));
      const form = forms.find((f) => String(f._id) === String(formId));
      if (form?.isDefault) {
        loadDefaultStats(form._id);
      }
    } catch (err) {
      setAllEntriesError(err.response?.data?.message || 'Failed to delete submission');
    } finally {
      setDeletingSubmission(false);
    }
  };

  const markEntryRead = async (entry, readState, form = null) => {
    const formToUse = form || selectedForm;
    if (!formToUse || !entry?._id) return;
    try {
      await api.patch(
        `/api/forms/${formToUse._id}/submissions/${entry._id}/read`,
        { read: readState },
        { params: scopedParams() }
      );
      setAllEntries((prev) =>
        prev.map((e) => (e._id === entry._id ? { ...e, read: readState } : e))
      );
      if (selectedEntry?._id === entry._id) {
        setSelectedEntry({ ...entry, read: readState });
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to update entry';
      setAllEntriesError(errorMsg);
    }
  };

  const addPersonToCrm = async (entry, form = null) => {
    const formId = form?._id || entry?.formId || selectedForm?._id;
    if (!formId || !entry?._id || !canOfferAddPersonToCrm(entry)) return;
    try {
      setAllEntriesError('');
      const { data } = await api.post(
        `/api/forms/${formId}/submissions/${entry._id}/add-to-crm`,
        {},
        { params: scopedParams() }
      );
      const leadId = data.leadId;
      const msg =
        data.matchedExisting === true
          ? 'Submission linked to an existing couple'
          : 'Added to couples';
      setSuccessMessage(msg);
      setAllEntries((prev) =>
        prev.map((e) =>
          e._id === entry._id
            ? { ...e, read: true, convertedLead: leadId, lead: leadId }
            : e
        )
      );
      if (selectedEntry?._id === entry._id) {
        setSelectedEntry((prev) =>
          prev ? { ...prev, read: true, convertedLead: leadId, lead: leadId } : null
        );
        setCrmLeadLookup({ status: 'done', leadId: String(leadId) });
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to add to couples';
      setAllEntriesError(errorMsg);
    }
  };

  const markEntryNotSpam = async (entry, form = null) => {
    const formId = form?._id || entry?.formId || selectedForm?._id;
    if (!formId || !entry?._id || clearingSpamId) return;
    try {
      setClearingSpamId(entry._id);
      const { data } = await api.post(
        `/api/forms/${formId}/submissions/${entry._id}/not-spam`,
        {},
        { params: scopedParams() }
      );
      const updated = data.submission || {};
      const patch = {
        spamStatus: updated.spamStatus || 'clear',
        deferredSteps: updated.deferredSteps,
        read: updated.read ?? entry.read,
        lead: updated.lead ?? entry.lead,
        convertedLead: updated.convertedLead ?? entry.convertedLead,
      };
      setAllEntries((prev) => prev.map((e) => (e._id === entry._id ? { ...e, ...patch } : e)));
      if (selectedEntry?._id === entry._id) {
        setSelectedEntry((prev) => (prev ? { ...prev, ...patch } : null));
      }
      if (Object.values(data.deferredSteps || {}).includes('failed')) {
        setError('Marked as not spam, but some follow-up steps did not finish. Please try again in a moment.');
      } else {
        setSuccessMessage('Marked as not spam');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update entry');
    } finally {
      setClearingSpamId(null);
    }
  };

  const renderNotSpamButton = (entry, form, buttonProps = {}) => {
    const potentialSpam = isPotentialSpam(entry);
    if (!potentialSpam && !hasFailedDeferredStep(entry)) return null;
    return (
      <Button
        size="small"
        variant="outlined"
        color="warning"
        disabled={clearingSpamId === entry._id}
        onClick={(e) => {
          e.stopPropagation();
          markEntryNotSpam(entry, form);
        }}
        {...buttonProps}
      >
        {potentialSpam ? 'Not spam' : 'Retry follow-up steps'}
      </Button>
    );
  };

  const handleCopyEmbedCode = () => {
    navigator.clipboard.writeText(embedCode)
      .then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      })
      .catch(_err => {});
  };

  const formatRelativeDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffYears > 0) {
      return `${diffYears} year${diffYears > 1 ? 's' : ''} ago`;
    } else if (diffMonths > 0) {
      return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
    } else if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else {
      const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
      if (diffHours > 0) {
        return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      } else {
        const diffMinutes = Math.floor(diffTime / (1000 * 60));
        return diffMinutes > 0 ? `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago` : 'Just now';
      }
    }
  };

  const _formatTableDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month}, ${year}`;
  };

  const filteredAndSortedForms = () => {
    let filtered = forms;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(form =>
        form.name.toLowerCase().includes(query) ||
        (form.description && form.description.toLowerCase().includes(query))
      );
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'response':
          aValue = formSubmissionCounts[a._id] || 0;
          bValue = formSubmissionCounts[b._id] || 0;
          break;
        case 'lastModified':
          aValue = new Date(a.updatedAt || a.createdAt).getTime();
          bValue = new Date(b.updatedAt || b.createdAt).getTime();
          break;
        case 'created':
          aValue = new Date(a.createdAt).getTime();
          bValue = new Date(b.createdAt).getTime();
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string') {
        return sortOrder === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      }
    });

    return sorted;
  };

  const filteredAndSortedEntries = () => {
    let filtered = [...allEntries];

    // Apply form filter
    if (selectedFormFilter !== 'all') {
      filtered = filtered.filter(entry => entry.formId === selectedFormFilter);
    }

    // Apply status filter
    if (selectedStatusFilter !== 'all') {
      filtered = filtered.filter(entry => {
        if (selectedStatusFilter === 'unread') {
          return !entry.read;
        } else if (selectedStatusFilter === 'read') {
          return entry.read && !(entry.convertedLead || entry.lead);
        } else if (selectedStatusFilter === 'converted') {
          return !!(entry.convertedLead || entry.lead);
        }
        return true;
      });
    }

    // Apply search filter
    if (entrySearchQuery) {
      const query = entrySearchQuery.toLowerCase();
      filtered = filtered.filter(entry => {
        // Search in form name
        if (entry.formName && entry.formName.toLowerCase().includes(query)) {
          return true;
        }
        // Search in entry data fields
        if (entry.data) {
          const dataString = JSON.stringify(entry.data).toLowerCase();
          if (dataString.includes(query)) {
            return true;
          }
        }
        return false;
      });
    }

    // Sort by date (newest first)
    filtered.sort((a, b) => {
      return new Date(b.createdAt) - new Date(a.createdAt);
    });

    return filtered;
  };

  // Render a field preview for the WYSIWYG editor
  const renderFieldPreview = (field, index, actualIndex, isCompulsory = false, fieldError = undefined, showFieldErr = false) => {
    const isSelected = selectedFieldIndex === actualIndex;
    const _fieldWidth = field.width || 100; // Default to 100% width
    const hasFieldIssue =
      showFieldErr &&
      fieldError != null &&
      fieldError !== '' &&
      (typeof fieldError === 'string' ||
        (typeof fieldError === 'object' && Object.keys(fieldError).length > 0));

    return (
      <Box
        sx={{
          p: 2,
          border: isSelected || hasFieldIssue ? '2px solid' : '1px solid',
          borderColor: hasFieldIssue
            ? 'error.main'
            : isSelected
              ? 'primary.main'
              : 'divider',
          borderRadius: 1,
          bgcolor: isSelected ? 'action.selected' : 'background.paper',
          cursor: isCompulsory ? 'default' : 'pointer',
          position: 'relative',
          width: '100%',
          transition: 'all 0.2s',
          '&:hover': {
            borderColor: hasFieldIssue
              ? 'error.main'
              : isCompulsory
                ? 'divider'
                : 'primary.main',
            bgcolor: isCompulsory ? 'background.paper' : 'action.hover',
            boxShadow: isCompulsory ? 'none' : 1,
          },
        }}
        onClick={() => {
          setSelectedFieldIndex(actualIndex);
        }}
      >
        {isCompulsory && (
          <Chip 
            label="Compulsory" 
            size="small" 
            color="primary" 
            sx={{ position: 'absolute', top: 8, right: 8 }}
          />
        )}
        {!(field.type === 'checkbox' && (!field.options || field.options.length === 0)) && (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
            {field.label || field.name}{field.required ? ' *' : ''}
          </Typography>
        )}
        {field.type === 'text' && (
          <TextField
            fullWidth
            size="small"
            placeholder={field.placeholder || ''}
            disabled
            variant="outlined"
          />
        )}
        {field.type === 'email' && (
          <TextField
            fullWidth
            size="small"
            type="email"
            placeholder={field.placeholder || 'you@example.com'}
            disabled
            variant="outlined"
          />
        )}
        {field.type === 'phone' && (
          <TextField
            fullWidth
            size="small"
            type="tel"
            placeholder={field.placeholder || 'Phone number'}
            disabled
            variant="outlined"
          />
        )}
        {field.type === 'textarea' && (
          <TextField
            fullWidth
            size="small"
            multiline
            rows={3}
            placeholder={field.placeholder || ''}
            disabled
            variant="outlined"
          />
        )}
        {field.type === 'date' && (
          <TextField
            fullWidth
            size="small"
            type="date"
            disabled
            variant="outlined"
            InputLabelProps={{ shrink: true }}
          />
        )}
        {field.type === 'select' && (
          <Box sx={{ pointerEvents: 'none' }}>
            <TextField
              fullWidth
              size="small"
              select
              disabled
              value=""
              variant="outlined"
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="">Select an option</MenuItem>
            </TextField>
            {(field.options || []).some((opt) => opt?.label || opt?.value) && (
              <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                {(field.options || []).map((opt, optIdx) => {
                  const label = (opt?.label || opt?.value || '').trim();
                  if (!label) return null;
                  return (
                    <Typography key={optIdx} variant="caption" color="text.secondary">
                      {label}
                    </Typography>
                  );
                })}
              </Box>
            )}
          </Box>
        )}
        {field.type === 'radio' && (
          <RadioGroup value="">
            {(field.options || []).slice(0, 4).map((opt, optIdx) => {
              const o = typeof opt === 'string' ? { label: opt, value: opt } : opt || {};
              const v = o.value != null ? String(o.value) : '';
              const lbl =
                o.label != null && o.label !== '' ? String(o.label) : v || `Option ${optIdx + 1}`;
              return (
                <FormControlLabel
                  key={optIdx}
                  value={v || lbl}
                  control={<Radio size="small" disabled />}
                  label={lbl}
                />
              );
            })}
          </RadioGroup>
        )}
        {field.type === 'checkbox' && (
          <Box>
            {(field.options || []).length > 0 ? (
              (field.options || []).map((opt, optIdx) => {
                const o = typeof opt === 'string' ? { label: opt, value: opt } : (opt || {});
                return (
                  <FormControlLabel
                    key={optIdx}
                    control={<Checkbox size="small" disabled />}
                    label={o.label != null && o.label !== '' ? o.label : o.value || 'Option'}
                  />
                );
              })
            ) : (
              <FormControlLabel
                control={<Checkbox size="small" disabled />}
                label={`${field.label || field.name || 'Checkbox'}${field.required ? ' *' : ''}`}
              />
            )}
          </Box>
        )}
      </Box>
    );
  };

  // Render forms tab content
  const renderForms = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      );
    }

    const sortedForms = filteredAndSortedForms();
    const websiteForms = sortedForms.filter((f) => f.kind !== 'questionnaire');
    const questionnaireForms = sortedForms.filter((f) => f.kind === 'questionnaire');

    const dashedCreateButtonSx = {
      py: 1.25,
      justifyContent: 'flex-start',
      textTransform: 'none',
      borderStyle: 'dashed',
      borderColor: 'divider',
      color: 'text.primary',
      '& .MuiButton-startIcon': { mr: 1 },
      '&:hover': {
        borderColor: '#f06292',
        bgcolor: '#fce4ec'
      }
    };

    const renderFormRow = (form) => {
      const responseCount = formSubmissionCounts[form._id] || 0;
      return (
        <TableRow
          key={form._id}
          hover
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate(`/supplier/forms/edit/${form._id}`)}
        >
          <TableCell>
            <Typography variant="body2" fontWeight={500}>
              {form.name}
            </Typography>
            {form.isDefault ? (
              <Typography variant="caption" color="text.secondary" display="block">
                Default form for My Couples
              </Typography>
            ) : null}
          </TableCell>
          <TableCell align="right">{responseCount}</TableCell>
          <TableCell>{formatRelativeDate(form.updatedAt || form.createdAt)}</TableCell>
          <TableCell align="right" onClick={(e) => e.stopPropagation()}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleMenuOpen(e, form._id);
              }}
              aria-label="Actions"
            >
              <DotsThreeVertical size={18} />
            </IconButton>
          </TableCell>
        </TableRow>
      );
    };

    const formBuilderSection = (
      <>
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
            Website forms
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
            Embed on your website to capture enquiries. Submissions appear under Form submissions; the default form can convert entries into couples on your list.
          </Typography>
          <Paper sx={{ overflow: 'hidden' }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell align="right">Responses</TableCell>
                    <TableCell>Updated</TableCell>
                    <TableCell align="right" width={56} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={4} sx={{ py: 1.5 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<Plus size={18} />}
                        onClick={() => goCreateForm('website')}
                        sx={dashedCreateButtonSx}
                      >
                        Create a website form
                      </Button>
                    </TableCell>
                  </TableRow>
                  {websiteForms.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary" align="center">
                          No website forms yet
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    websiteForms.map((form) => renderFormRow(form))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
            Questionnaires
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
            Copy a shareable link to use anywhere, or email a one-off private link to a couple from your list. Responses appear under Form submissions (and as a couple when the respondent is not already on your list).
          </Typography>
          <Paper sx={{ overflow: 'hidden' }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell align="right">Responses</TableCell>
                    <TableCell>Updated</TableCell>
                    <TableCell align="right" width={56} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={4} sx={{ py: 1.5 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<Plus size={18} />}
                        onClick={() => goCreateForm('questionnaire')}
                        sx={dashedCreateButtonSx}
                      >
                        Create a questionnaire
                      </Button>
                    </TableCell>
                  </TableRow>
                  {questionnaireForms.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary" align="center">
                          No questionnaires yet
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    questionnaireForms.map((form) => renderFormRow(form))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      </>
    );

    const formSubmissionsSection = (
        <Box sx={{ mt: 0 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'flex-end', 
            alignItems: { xs: 'stretch', sm: 'center' }, 
            mb: 2, 
            gap: { xs: 2, sm: 2 }, 
            flexWrap: 'wrap' 
          }}>
            <Box sx={{ 
              display: 'flex', 
              gap: { xs: 1, sm: 2 }, 
              alignItems: 'center', 
              flex: { xs: '1 1 100%', sm: 1 }, 
              justifyContent: { xs: 'stretch', sm: 'flex-end' },
              flexWrap: 'wrap',
              width: { xs: '100%', sm: 'auto' }
            }}>
              <TextField
                size="small"
                placeholder="Search entries..."
                value={entrySearchQuery}
                onChange={(e) => setEntrySearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MagnifyingGlass size={20} />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  minWidth: { xs: '100%', sm: 200 },
                  flex: { xs: '1 1 100%', sm: '0 0 auto' }
                }}
              />
              <Select
                size="small"
                value={selectedFormFilter}
                onChange={(e) => setSelectedFormFilter(e.target.value)}
                sx={{ 
                  minWidth: { xs: '100%', sm: 180 },
                  flex: { xs: '1 1 100%', sm: '0 0 auto' }
                }}
              >
                <MenuItem value="all">All Forms</MenuItem>
                {forms.map((form) => (
                  <MenuItem key={form._id} value={form._id}>
                    {form.name}
                  </MenuItem>
                ))}
              </Select>
              <Select
                size="small"
                value={selectedStatusFilter}
                onChange={(e) => setSelectedStatusFilter(e.target.value)}
                sx={{ 
                  minWidth: { xs: '100%', sm: 150 },
                  flex: { xs: '1 1 100%', sm: '0 0 auto' }
                }}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="unread">Unread</MenuItem>
                <MenuItem value="read">Read</MenuItem>
                <MenuItem value="converted">Converted</MenuItem>
              </Select>
              <Button
                variant="outlined"
                size="small"
                onClick={fetchAllEntries}
                disabled={allEntriesLoading}
                startIcon={allEntriesLoading ? <CircularProgress size={16} /> : null}
                sx={{ 
                  flex: { xs: '1 1 100%', sm: '0 0 auto' },
                  width: { xs: '100%', sm: 'auto' }
                }}
              >
                Refresh
              </Button>
            </Box>
          </Box>

          {allEntriesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : allEntriesError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {allEntriesError}
            </Alert>
          ) : (() => {
            const filteredEntries = filteredAndSortedEntries();

            if (allEntries.length === 0) {
              return (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    No responses yet. Submissions from your forms will show up here.
                  </Typography>
                </Paper>
              );
            }
            
            if (filteredEntries.length === 0) {
              return (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    {entrySearchQuery || selectedFormFilter !== 'all' 
                      ? 'No entries match your filters.' 
                      : 'No responses yet. Submissions from your forms will show up here.'}
                  </Typography>
                </Paper>
              );
            }
            
            return (
              <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow
                        sx={{
                          bgcolor: 'action.hover',
                          '& th': {
                            fontWeight: 600,
                            borderBottom: 'none',
                            py: 1.5,
                          },
                          '& th:first-of-type': {
                            borderTopLeftRadius: 8,
                          },
                          '& th:last-of-type': {
                            borderTopRightRadius: 8,
                          },
                        }}
                      >
                        <TableCell>Name / Email</TableCell>
                        <TableCell>Form Name</TableCell>
                        <TableCell>Submitted</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                    {filteredEntries.map((entry) => {
                    const form = forms.find(f => f._id === entry.formId);
                    const entryCrmLeadId = getCrmLeadIdFromEntry(entry);
                    const submitterName = getSubmissionNameForDisplay(entry.data || {});
                    const submitterEmail = getSubmissionEmailForCrm(entry.data || {});
                    return (
                      <TableRow
                        key={entry._id}
                        hover
                        onClick={() => {
                          setSelectedEntry(entry);
                          setSelectedForm(form || null);
                        }}
                        selected={selectedEntry?._id === entry._id}
                        sx={{
                          cursor: 'pointer',
                          opacity: entry.read ? 0.5 : 1
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {submitterName || 'Unknown contact'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {submitterEmail || 'No email provided'}
                          </Typography>
                          {isPotentialSpam(entry) && (
                            <Box sx={{ mt: 0.5 }}>
                              <PotentialSpamChip />
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {entry.formName || 'Unknown Form'}
                          </Typography>
                        </TableCell>
                        <TableCell>{new Date(entry.createdAt).toLocaleString()}</TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedEntry(entry);
                                setSelectedForm(form || null);
                              }}
                            >
                              View
                            </Button>
                            {renderNotSpamButton(entry, form)}
                            {isPotentialSpam(entry) ? null : entryCrmLeadId ? (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/supplier/crm/leads/${entryCrmLeadId}`);
                                }}
                              >
                                View in couples
                              </Button>
                            ) : canOfferAddPersonToCrm(entry) ? (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  addPersonToCrm(entry, form);
                                }}
                              >
                                Add to couples
                              </Button>
                            ) : null}
                            <Button
                              size="small"
                              variant="text"
                              onClick={(e) => {
                                e.stopPropagation();
                                markEntryRead(entry, !entry.read, form);
                              }}
                            >
                              {entry.read ? 'Mark unread' : 'Mark read'}
                            </Button>
                            <Button
                              size="small"
                              variant="text"
                              color="error"
                              onClick={(e) => requestDeleteSubmission(entry, e)}
                            >
                              Delete
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                    })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={entriesTotalCount}
                  page={Math.max(entriesPage - 1, 0)}
                  onPageChange={(_, nextPage) => setEntriesPage(nextPage + 1)}
                  rowsPerPage={entriesRowsPerPage}
                  onRowsPerPageChange={(event) => {
                    const nextLimit = parseInt(event.target.value, 10) || 20;
                    setEntriesRowsPerPage(nextLimit);
                    setEntriesPage(1);
                  }}
                  rowsPerPageOptions={[10, 20, 50, 100]}
                />
              </Paper>
            );
          })()}
          <FormSubmissionEntryDialog
            open={Boolean(selectedEntry)}
            onClose={() => {
              setSelectedEntry(null);
              setSelectedForm(null);
            }}
            entry={selectedEntry}
            form={selectedForm}
            footer={
              (() => {
                const resolvedCrmLeadId =
                  getCrmLeadIdFromEntry(selectedEntry) || crmLeadLookup.leadId;
                return (
                  <>
                    {resolvedCrmLeadId ? (
                      <Button
                        variant="outlined"
                        onClick={() => navigate(`/supplier/crm/leads/${resolvedCrmLeadId}`)}
                      >
                        View in couples
                      </Button>
                    ) : null}
                    {selectedEntry
                      ? renderNotSpamButton(selectedEntry, selectedForm, { size: 'medium' })
                      : null}
                    {canOfferAddPersonToCrm(selectedEntry) &&
                      !isPotentialSpam(selectedEntry) &&
                      crmLeadLookup.status === 'loading' && (
                        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                          Checking couples…
                        </Typography>
                      )}
                    {selectedEntry &&
                      canOfferAddPersonToCrm(selectedEntry) &&
                      !isPotentialSpam(selectedEntry) &&
                      !resolvedCrmLeadId &&
                      crmLeadLookup.status === 'done' && (
                        <Button variant="contained" onClick={() => addPersonToCrm(selectedEntry, selectedForm)}>
                          Add to couples
                        </Button>
                      )}
                    {selectedEntry ? (
                      <Button
                        variant="text"
                        color="error"
                        onClick={() => requestDeleteSubmission(selectedEntry)}
                      >
                        Delete
                      </Button>
                    ) : null}
                  </>
                );
              })()
            }
          />
        </Box>
    );

    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            mb: 2,
            flexWrap: 'wrap',
          }}
        >
          <Typography
            component="h2"
            className="pagename"
            sx={{ ...styles.text.pagename, mb: 0, flex: '1 1 auto', minWidth: 0 }}
          >
            {formsView === 'builder' ? 'Form builder' : 'Form submissions'}
          </Typography>
          <Button
            variant="outlined"
            size="medium"
            onClick={() =>
              navigate(
                formsView === 'builder'
                  ? '/supplier/forms/submissions'
                  : '/supplier/forms/builder'
              )
            }
            sx={{
              flexShrink: 0,
              textTransform: 'none',
              borderColor: 'common.black',
              color: 'common.black',
              '&:hover': {
                borderColor: 'common.black',
                bgcolor: 'action.hover',
              },
            }}
          >
            {formsView === 'builder' ? 'Form submissions' : 'Form builder'}
          </Button>
        </Box>
        {formsView === 'submissions' ? formSubmissionsSection : formBuilderSection}
      </Box>
    );
  };


  const _toggleFormActive = async (form) => {
    try {
      const updatedForm = { ...form, isActive: !form.isActive };
      await api.put(`/api/forms/${form._id}`, updatedForm, { params: scopedParams() });
      const updatedForms = forms.map(f => f._id === form._id ? updatedForm : f);
      setForms(updatedForms);
      setSuccessMessage(`Form ${updatedForm.isActive ? 'activated' : 'deactivated'} successfully`);
      // Refresh submission counts after update
      fetchSubmissionCounts(updatedForms);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update form status');
    }
  };

  const renderFormEditor = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      );
    }

    // Form validation schema
    const FormSchema = Yup.object().shape({
      name: Yup.string().required('Form name is required'),
      description: Yup.string(),
      kind: Yup.string().oneOf(['website', 'questionnaire']).optional(),
      allowedDomains: Yup.string().optional(),
      emailNotification: Yup.object().shape({
        recipientEmail: Yup.string()
          .transform((v) => (v == null ? '' : String(v).trim()))
          .required('Notification email is required')
          .email('Enter a valid email address')
      }),
      mailchimp: Yup.object().shape({
        enabled: Yup.boolean(),
        consentMessage: Yup.string().when('enabled', {
          is: true,
          then: (schema) =>
            schema
              .transform((v) => (v == null ? '' : String(v).trim()))
              .min(10, 'Add clear consent wording (at least 10 characters)')
              .max(4000, 'Keep consent wording under 4,000 characters')
        })
      }),
      fields: Yup.array().of(
        Yup.object().shape({
          label: Yup.string().required('Field label is required'),
          name: Yup.string().required('Field name is required'),
          type: Yup.string().required('Field type is required'),
          required: Yup.boolean(),
          placeholder: Yup.string(),
          width: Yup.number().min(25).max(100),
          options: Yup.array().when('type', {
            is: (type) => ['select', 'radio', 'checkbox'].includes(type),
            then: () => Yup.array().of(
              Yup.object().shape({
                label: Yup.string().required('Option label is required'),
                value: Yup.string().required('Option value is required')
              })
            ).min(1, 'At least one option is required')
          })
        })
      )
    });

    // Initial form data with at least one field for new forms
    const initialFormData = {
      ...formData,
      kind: formData.kind || 'website',
      emailNotification: {
        enabled: formData.emailNotification?.enabled !== false,
        subject: formData.emailNotification?.subject || 'New enquiry from your website',
        template: formData.emailNotification?.template || 'default',
        recipientEmail: formData.emailNotification?.recipientEmail || ''
      },
      mailchimp: {
        enabled: formData.mailchimp?.enabled === true,
        consentMessage:
          formData.mailchimp?.consentMessage != null && String(formData.mailchimp.consentMessage).trim()
            ? String(formData.mailchimp.consentMessage).trim()
            : DEFAULT_MAILCHIMP_CONSENT_MESSAGE
      },
      allowedDomains: filterDomainsForFormInput(formData.allowedDomains || []),
      fields: formData.fields && formData.fields.length > 0 ? formData.fields.map(f => ({
        ...f,
        width: f.width || 100,
        options: normalizeFieldOptions(f)
      })) : [
        {
          type: 'text',
          name: 'name',
          label: 'Name',
          required: true,
          placeholder: 'Enter your name',
          options: [],
          width: 100
        }
      ]
    };

    return (
      <Paper elevation={1} sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Formik
          enableReinitialize
          initialValues={initialFormData}
          validationSchema={FormSchema}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              const parsedAllowed = (values.allowedDomains || '')
                .split(',')
                .map(s => parseWebsiteHostname(s))
                .filter(Boolean);
              const kindVal = values.kind === 'questionnaire' ? 'questionnaire' : 'website';

              if (formAction === 'create') {
                const response = await api.post('/api/forms', {
                  ...values,
                  kind: kindVal,
                  allowedDomains: kindVal === 'questionnaire' ? [] : parsedAllowed,
                  businessId
                });
                setSuccessMessage('Form created successfully');
                // Update formId for newly created form so user can continue editing
                if (response.data?._id) {
                  navigate(`/supplier/forms/edit/${response.data._id}`, { replace: true });
                }
              } else {
                const response = await api.put(
                  `/api/forms/${formId}`,
                  {
                    ...values,
                    kind: kindVal,
                    allowedDomains: kindVal === 'questionnaire' ? [] : parsedAllowed,
                    businessId
                  },
                  { params: scopedParams() }
                );
                setSuccessMessage('Form updated successfully');
                // Keep the editor on the saved form. Resetting to the pre-save values
                // dropped new fields such as dropdown options immediately after save.
                if (response.data) {
                  setFormData({ ...response.data, kind: response.data.kind || 'website' });
                }
              }
              setShowFormValidationErrors(false);
            } catch (err) {
              setError('Failed to save form');
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ values, errors, touched, isSubmitting, handleChange, setFieldValue, submitForm, validateForm }) => {
            // Get all fields including compulsory for preview
            const allFieldsForPreview = values.fields;
            const mailchimpAvailable = mailchimpStatus.connected && mailchimpStatus.hasAudience;

            const handleDragEnd = (result) => {
              if (!result.destination) return;
              
              const sourceIndex = result.source.index;
              const destIndex = result.destination.index;
              
              if (sourceIndex === destIndex) return;
              
              // Reorder fields (including compulsory fields)
              const newFields = [...values.fields];
              const [removed] = newFields.splice(sourceIndex, 1);
              newFields.splice(destIndex, 0, removed);
              
              setFieldValue('fields', newFields);
            };

            const fieldInputShowErr = (touchedPart, errVal) =>
              Boolean(errVal && (touchedPart || showFormValidationErrors));

            const profileWebsiteDisplay = parseWebsiteHostname(
              selectedBusiness?.business?.contactInfo?.website || ''
            );

            const handleSaveClick = async (e) => {
              if (e?.preventDefault) e.preventDefault();
              const validationErrors = await validateForm();

              if (validationErrors && Object.keys(validationErrors).length > 0) {
                setShowFormValidationErrors(true);
                if (
                  validationErrors.fields &&
                  Array.isArray(validationErrors.fields) &&
                  !validationErrors.name &&
                  !validationErrors.allowedDomains
                ) {
                  const idx = validationErrors.fields.findIndex((fe) => {
                    if (fe == null || fe === undefined) return false;
                    if (typeof fe === 'string') return fe.length > 0;
                    if (typeof fe === 'object') return Object.keys(fe).length > 0;
                    return false;
                  });
                  if (idx >= 0) setSelectedFieldIndex(idx);
                }
                const firstErrorField = Object.keys(validationErrors)[0];
                const firstError = validationErrors[firstErrorField];

                let errorMessage = 'Please fix the form errors before submitting';
                if (firstError) {
                  if (typeof firstError === 'string') {
                    errorMessage = firstError;
                  } else if (typeof firstError === 'object') {
                    const findFirstStringError = (obj) => {
                      if (typeof obj === 'string') return obj;
                      if (Array.isArray(obj)) {
                        for (const item of obj) {
                          const found = findFirstStringError(item);
                          if (found) return found;
                        }
                      } else if (obj && typeof obj === 'object') {
                        for (const key in obj) {
                          const found = findFirstStringError(obj[key]);
                          if (found) return found;
                        }
                      }
                      return null;
                    };
                    errorMessage = findFirstStringError(firstError) || errorMessage;
                  }
                }

                setError(errorMessage);

                setTimeout(() => {
                  const errorField = document.querySelector(`[name="${firstErrorField}"]`);
                  if (errorField) {
                    errorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    errorField.focus();
                  }
                }, 100);

                return;
              }

              setShowFormValidationErrors(false);
              submitForm();
            };

            return (
              <>
                <FormDirtySync onDirtyChange={setFormEditorDirty} />
                <Form>
                {/* WYSIWYG Form Builder — desktop preview scrolls inside panel; mobile uses page scroll */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: { xs: 2, md: 0 },
                    alignItems: { xs: 'stretch', md: 'flex-start' },
                    width: '100%'
                  }}
                >
                  {/* Left: visual form builder */}
                  <Box
                    sx={{
                      flex: { xs: '0 0 auto', md: 1 },
                      width: { xs: '100%', md: 'auto' },
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'flex 0.3s ease-in-out',
                      minWidth: 0
                    }}
                  >
                    <Paper
                      sx={{
                        p: { xs: 2, sm: 3 },
                        overflow: { xs: 'visible', md: 'auto' },
                        bgcolor: 'grey.50',
                        border: '2px dashed',
                        borderColor: 'divider',
                        // Desktop: capped height so long forms scroll inside the preview
                        minHeight: {
                          xs: 'auto',
                          md: 'min(560px, calc(100vh - 420px))'
                        },
                        maxHeight: { xs: 'none', md: 'calc(100vh - 420px)' },
                        height: { xs: 'fit-content', md: 'auto' }
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: 800,
                          mx: 'auto',
                          bgcolor: 'background.paper',
                          p: { xs: 2, sm: 3, md: 4 },
                          borderRadius: 1,
                          boxShadow: 1
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: values.description ? 0 : 1 }}>
                          <Typography variant="h5" component="span" sx={{ flex: 1, lineHeight: 1.3 }}>
                            {values.name || 'Form name'}
                          </Typography>
                          {isDefaultForm && (
                            <Tooltip
                              title="The compulsory fields (Name, Email, Phone, Wedding Date, Message) are fixed and cannot be edited or removed, but you can reorder them. You can add additional fields below."
                              arrow
                              enterTouchDelay={0}
                            >
                              <IconButton
                                size="small"
                                aria-label="About compulsory fields"
                                sx={{ mt: -0.25, color: 'text.secondary' }}
                              >
                                <Info size={18} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        {values.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            {values.description}
                          </Typography>
                        )}
                        
                        <DragDropContext onDragEnd={handleDragEnd}>
                          <Droppable droppableId="form-fields">
                            {(provided) => (
                              <Box 
                                {...provided.droppableProps} 
                                ref={provided.innerRef}
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 2,
                                  mb: 2,
                                  alignItems: 'flex-start'
                                }}
                              >
                                {allFieldsForPreview.map((field, actualIndex) => {
                                  const isCompulsory = isCompulsoryField(field.name);
                                  // Calculate draggable index for all fields (including compulsory)
                                  const draggableIndex = actualIndex;
                                  
                                  return (
                                    <Draggable 
                                      key={actualIndex} 
                                      draggableId={`field-${actualIndex}`} 
                                      index={draggableIndex}
                                    >
                                      {(provided, snapshot) => {
                                        const fieldWidth = field.width || 100;
                                        // Calculate width accounting for gap (gap: 2 = 16px, so subtract 8px from each field)
                                        const widthStyle = fieldWidth === 100 
                                          ? '100%' 
                                          : `calc(${fieldWidth}% - 8px)`;
                                        return (
                                          <Box
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            onClick={() => {
                                              setSelectedFieldIndex(actualIndex);
                                            }}
                                            sx={{
                                              opacity: snapshot.isDragging ? 0.5 : 1,
                                              width: widthStyle,
                                              flexBasis: widthStyle,
                                              minWidth: fieldWidth <= 50 ? '150px' : '200px',
                                              flexShrink: 0,
                                              maxWidth: widthStyle,
                                              position: 'relative',
                                              zIndex: snapshot.isDragging ? 1000 : 1,
                                              cursor: 'pointer'
                                            }}
                                          >
                                            <Box
                                              {...provided.dragHandleProps}
                                              onMouseDown={(e) => {
                                                e.stopPropagation();
                                              }}
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                mb: 0.5,
                                                cursor: 'grab',
                                                userSelect: 'none',
                                                touchAction: 'none',
                                                opacity: isCompulsory ? 0.7 : 1,
                                                '&:active': {
                                                  cursor: 'grabbing'
                                                }
                                              }}
                                            >
                                              <DotsSixVertical size={16} />
                                              <Typography variant="caption" color="text.secondary">
                                                {isCompulsory ? 'Compulsory Field - Drag to reorder' : 'Drag to reorder'}
                                              </Typography>
                                            </Box>
                                            {renderFieldPreview(
                                              field,
                                              draggableIndex,
                                              actualIndex,
                                              isCompulsory,
                                              errors.fields?.[actualIndex],
                                              showFormValidationErrors
                                            )}
                                          </Box>
                                        );
                                      }}
                                    </Draggable>
                                  );
                                })}
                                {provided.placeholder}
                              </Box>
                            )}
                          </Droppable>
                        </DragDropContext>

                        {values.kind !== 'questionnaire' && mailchimpStatus.connected && values.mailchimp?.enabled ? (
                          <Box sx={{ width: '100%', mb: 2, p: 2, borderRadius: 1, border: '1px dashed', borderColor: 'divider' }}>
                            <FormControlLabel
                              control={<Checkbox size="small" disabled />}
                              label={
                                String(values.mailchimp?.consentMessage || '').trim() ||
                                DEFAULT_MAILCHIMP_CONSENT_MESSAGE
                              }
                            />
                            <Typography variant="caption" color="text.secondary" display="block">
                              This consent line appears automatically on your live and embedded form, just above Submit.
                            </Typography>
                          </Box>
                        ) : null}

                        <Button
                          variant="outlined"
                          fullWidth
                          startIcon={<Plus />}
                          sx={{ mt: 2 }}
                          onClick={() => {
                            const newField = {
                              type: 'text',
                              name: '',
                              label: '',
                              required: false,
                              placeholder: '',
                              options: [],
                              width: 100
                            };
                            if (!newField.width) {
                              newField.width = 100;
                            }
                            if (isDefaultForm) {
                              const lastCompulsoryIndex = values.fields.findIndex((f, idx) => {
                                const nextField = values.fields[idx + 1];
                                return isCompulsoryField(f.name) && (!nextField || !isCompulsoryField(nextField.name));
                              });
                              const insertIndex = lastCompulsoryIndex >= 0 ? lastCompulsoryIndex + 1 : values.fields.length;
                              const newFields = [...values.fields];
                              newFields.splice(insertIndex, 0, newField);
                              setFieldValue('fields', newFields);
                              setSelectedFieldIndex(insertIndex);
                            } else {
                              setFieldValue('fields', [...values.fields, newField]);
                              setSelectedFieldIndex(values.fields.length);
                            }
                          }}
                        >
                          Add field
                        </Button>

                        <Button
                          variant="contained"
                          fullWidth
                          sx={{ mt: 2 }}
                          disabled
                        >
                          {values.styling?.buttonText || 'Submit'}
                        </Button>
                      </Box>
                    </Paper>
                  </Box>

                  {/* Right (desktop) / below (mobile): form details + actions, or field properties */}
                  <Box
                    sx={{
                      width: { xs: '100%', md: 400 },
                      maxWidth: { xs: '100%', md: 400 },
                      flexShrink: 0,
                      bgcolor: 'background.paper',
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: { xs: 'visible', md: 'hidden' },
                      minHeight: { xs: 'auto', md: 0 },
                      alignSelf: { xs: 'stretch', md: 'stretch' }
                    }}
                  >
                    <Field type="hidden" name="kind" />
                    {selectedFieldIndex !== null &&
                    selectedFieldIndex !== undefined &&
                    values.fields &&
                    values.fields[selectedFieldIndex] ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                        <Box sx={{ p: 3, pb: 2, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch', mb: 2 }}>
                            <Button
                              variant="contained"
                              type="button"
                              disabled={isSubmitting}
                              sx={{ flex: 1 }}
                              startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                              onClick={handleSaveClick}
                            >
                              {formAction === 'create' ? 'Create form' : 'Save changes'}
                            </Button>
                            {values.kind !== 'questionnaire' && (
                              <Tooltip title="Embedding settings">
                                <IconButton
                                  onClick={() => setAllowedDomainsSettingsOpen(true)}
                                  aria-label="Embedding settings"
                                  sx={{
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    flexShrink: 0,
                                    alignSelf: 'stretch',
                                    height: 'auto',
                                    minWidth: 48
                                  }}
                                >
                                  <Gear size={22} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                          <Button
                            size="small"
                            fullWidth
                            startIcon={<CaretLeft size={18} />}
                            onClick={() => setSelectedFieldIndex(null)}
                            sx={{ textTransform: 'none' }}
                          >
                            Form actions
                          </Button>
                        </Box>
                        <Box sx={{ p: 3, overflow: 'auto', flex: 1, pt: 2 }}>
                          <Typography variant="h6" sx={{ mb: 2 }}>
                            Field properties
                          </Typography>

                          <FieldArray name="fields">
                            {({ remove }) => {
                              const fieldIndex = selectedFieldIndex;
                              const currentField = values.fields[fieldIndex];
                              return (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  {isCompulsoryField(currentField?.name) && (
                                    <Alert severity="info" sx={{ mb: 1 }}>
                                      This is a compulsory field. You can edit its width, label, and placeholder, but the name and type cannot be changed.
                                    </Alert>
                                  )}
                                  <Field
                                    as={TextField}
                                    fullWidth
                                    name={`fields[${fieldIndex}].label`}
                                    label="Field label"
                                    variant="outlined"
                                    disabled={false}
                                    error={fieldInputShowErr(
                                      touched.fields?.[fieldIndex]?.label,
                                      errors.fields?.[fieldIndex]?.label
                                    )}
                                    helperText={
                                      fieldInputShowErr(
                                        touched.fields?.[fieldIndex]?.label,
                                        errors.fields?.[fieldIndex]?.label
                                      ) && errors.fields?.[fieldIndex]?.label
                                    }
                                  />

                                  <Field
                                    as={TextField}
                                    fullWidth
                                    name={`fields[${fieldIndex}].name`}
                                    label="Field name"
                                    variant="outlined"
                                    placeholder="E.g., email, name, phone"
                                    disabled={isCompulsoryField(currentField?.name)}
                                    error={fieldInputShowErr(
                                      touched.fields?.[fieldIndex]?.name,
                                      errors.fields?.[fieldIndex]?.name
                                    )}
                                    helperText={
                                      fieldInputShowErr(
                                        touched.fields?.[fieldIndex]?.name,
                                        errors.fields?.[fieldIndex]?.name
                                      ) && errors.fields?.[fieldIndex]?.name
                                    }
                                  />

                                  <FormControl
                                    fullWidth
                                    error={fieldInputShowErr(
                                      touched.fields?.[fieldIndex]?.type,
                                      errors.fields?.[fieldIndex]?.type
                                    )}
                                  >
                                    <InputLabel id={`field-type-label-${fieldIndex}`}>Field type</InputLabel>
                                    <Field
                                      as={Select}
                                      name={`fields[${fieldIndex}].type`}
                                      labelId={`field-type-label-${fieldIndex}`}
                                      label="Field type"
                                      disabled={isCompulsoryField(currentField?.name)}
                                      onChange={(e) => {
                                        const newType = e.target.value;
                                        handleChange(e);
                                        if (
                                          ['select', 'radio', 'checkbox'].includes(newType) &&
                                          (!values.fields[fieldIndex].options ||
                                            values.fields[fieldIndex].options.length === 0)
                                        ) {
                                          setFieldValue(`fields[${fieldIndex}].options`, [{ label: '', value: '' }]);
                                        }
                                      }}
                                    >
                                      <MenuItem value="text">Text</MenuItem>
                                      <MenuItem value="email">Email</MenuItem>
                                      <MenuItem value="phone">Phone</MenuItem>
                                      <MenuItem value="textarea">Text area</MenuItem>
                                      <MenuItem value="select">Dropdown</MenuItem>
                                      <MenuItem value="radio">Radio buttons</MenuItem>
                                      <MenuItem value="checkbox">Checkboxes</MenuItem>
                                      <MenuItem value="date">Date</MenuItem>
                                    </Field>
                                    {fieldInputShowErr(
                                      touched.fields?.[fieldIndex]?.type,
                                      errors.fields?.[fieldIndex]?.type
                                    ) &&
                                      errors.fields?.[fieldIndex]?.type && (
                                        <FormHelperText>{errors.fields[fieldIndex].type}</FormHelperText>
                                      )}
                                  </FormControl>

                                  <Field
                                    as={TextField}
                                    fullWidth
                                    name={`fields[${fieldIndex}].placeholder`}
                                    label="Placeholder text"
                                    variant="outlined"
                                  />

                                  <Box>
                                    <Typography variant="body2" gutterBottom>
                                      Field width: {currentField?.width || 100}%
                                    </Typography>
                                    <Slider
                                      value={currentField?.width || 100}
                                      onChange={(e, value) => {
                                        setFieldValue(`fields[${fieldIndex}].width`, value);
                                      }}
                                      min={25}
                                      max={100}
                                      step={25}
                                      marks={[
                                        { value: 25, label: '25%' },
                                        { value: 50, label: '50%' },
                                        { value: 75, label: '75%' },
                                        { value: 100, label: '100%' }
                                      ]}
                                    />
                                  </Box>

                                  <FormControlLabel
                                    control={
                                      <Field
                                        as={Switch}
                                        name={`fields[${fieldIndex}].required`}
                                        color="primary"
                                        checked={values.fields[fieldIndex]?.required || false}
                                      />
                                    }
                                    label="Required field"
                                  />

                                  {['select', 'radio', 'checkbox'].includes(currentField?.type) && (
                                    <Box>
                                      <Typography variant="subtitle2" gutterBottom>
                                        Options
                                      </Typography>
                                      {typeof errors.fields?.[fieldIndex]?.options === 'string' &&
                                        fieldInputShowErr(true, errors.fields?.[fieldIndex]?.options) && (
                                          <Typography variant="caption" color="error" display="block" sx={{ mb: 1 }}>
                                            {errors.fields[fieldIndex].options}
                                          </Typography>
                                        )}
                                      <FieldArray
                                        key={`field-options-${fieldIndex}`}
                                        name={`fields[${fieldIndex}].options`}
                                      >
                                        {({ push: pushOption, remove: removeOption }) => (
                                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            {currentField?.options && currentField.options.length > 0
                                              ? currentField.options.map((option, optionIndex) => (
                                                  <Box key={optionIndex} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                                    <Field
                                                      as={TextField}
                                                      fullWidth
                                                      size="small"
                                                      label="Label"
                                                      name={`fields[${fieldIndex}].options[${optionIndex}].label`}
                                                      error={fieldInputShowErr(
                                                        touched.fields?.[fieldIndex]?.options?.[optionIndex]?.label,
                                                        errors.fields?.[fieldIndex]?.options?.[optionIndex]?.label
                                                      )}
                                                      helperText={
                                                        fieldInputShowErr(
                                                          touched.fields?.[fieldIndex]?.options?.[optionIndex]?.label,
                                                          errors.fields?.[fieldIndex]?.options?.[optionIndex]?.label
                                                        ) && errors.fields?.[fieldIndex]?.options?.[optionIndex]?.label
                                                      }
                                                    />
                                                    <Field
                                                      as={TextField}
                                                      fullWidth
                                                      size="small"
                                                      label="Value"
                                                      name={`fields[${fieldIndex}].options[${optionIndex}].value`}
                                                      error={fieldInputShowErr(
                                                        touched.fields?.[fieldIndex]?.options?.[optionIndex]?.value,
                                                        errors.fields?.[fieldIndex]?.options?.[optionIndex]?.value
                                                      )}
                                                      helperText={
                                                        fieldInputShowErr(
                                                          touched.fields?.[fieldIndex]?.options?.[optionIndex]?.value,
                                                          errors.fields?.[fieldIndex]?.options?.[optionIndex]?.value
                                                        ) && errors.fields?.[fieldIndex]?.options?.[optionIndex]?.value
                                                      }
                                                    />
                                                    <IconButton
                                                      size="small"
                                                      color="error"
                                                      disabled={currentField?.options?.length <= 1}
                                                      onClick={() => removeOption(optionIndex)}
                                                      sx={{ mt: 0.5 }}
                                                    >
                                                      <X size={18} />
                                                    </IconButton>
                                                  </Box>
                                                ))
                                              : null}
                                            <Button
                                              size="small"
                                              variant="outlined"
                                              startIcon={<Plus size={16} />}
                                              onClick={() => pushOption({ label: '', value: '' })}
                                            >
                                              Add option
                                            </Button>
                                          </Box>
                                        )}
                                      </FieldArray>
                                    </Box>
                                  )}

                                  {!isCompulsoryField(currentField?.name) && (
                                    <Button
                                      variant="outlined"
                                      color="error"
                                      startIcon={<Trash size={18} />}
                                      onClick={() => {
                                        remove(fieldIndex);
                                        setSelectedFieldIndex(null);
                                      }}
                                    >
                                      Delete field
                                    </Button>
                                  )}
                                </Box>
                              );
                            }}
                          </FieldArray>
                        </Box>
                      </Box>
                    ) : (
                      <>
                        <Box
                          sx={{
                            p: 3,
                            pb: 2,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            flexShrink: 0
                          }}
                        >
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                            Form details
                          </Typography>
                          <Field
                            as={TextField}
                            fullWidth
                            name="name"
                            label="Form name"
                            variant="outlined"
                            size="small"
                            margin="dense"
                            sx={{ mb: 1.5 }}
                            error={fieldInputShowErr(touched.name, errors.name)}
                            helperText={fieldInputShowErr(touched.name, errors.name) && errors.name}
                          />
                          <Field
                            as={TextField}
                            fullWidth
                            name="description"
                            label="Description"
                            variant="outlined"
                            size="small"
                            margin="dense"
                            multiline
                            minRows={2}
                            error={fieldInputShowErr(touched.description, errors.description)}
                            helperText={
                              fieldInputShowErr(touched.description, errors.description) && errors.description
                            }
                          />
                          <Field
                            as={TextField}
                            fullWidth
                            name="emailNotification.recipientEmail"
                            label="Notification email"
                            placeholder="you@example.com"
                            variant="outlined"
                            size="small"
                            margin="dense"
                            type="email"
                            autoComplete="email"
                            error={fieldInputShowErr(
                              touched.emailNotification?.recipientEmail,
                              errors.emailNotification?.recipientEmail
                            )}
                            helperText={
                              fieldInputShowErr(
                                touched.emailNotification?.recipientEmail,
                                errors.emailNotification?.recipientEmail
                              ) && errors.emailNotification?.recipientEmail
                                ? errors.emailNotification.recipientEmail
                                : 'Required. New submission notifications are sent to this address only.'
                            }
                          />
                          {values.kind !== 'questionnaire' && mailchimpStatus.connected ? (
                            <>
                              <FormControlLabel
                                sx={{ mt: 1, mb: 0.5 }}
                                control={
                                  <Switch
                                    checked={values.mailchimp?.enabled || false}
                                    onChange={(event) => {
                                      const enabled = event.target.checked;
                                      if (!enabled) {
                                        setFieldValue('mailchimp', { enabled: false, consentMessage: '' });
                                        return;
                                      }
                                      const existing = String(values.mailchimp?.consentMessage || '').trim();
                                      setFieldValue('mailchimp', {
                                        enabled: true,
                                        consentMessage: existing || DEFAULT_MAILCHIMP_CONSENT_MESSAGE
                                      });
                                    }}
                                    disabled={!mailchimpAvailable}
                                  />
                                }
                                label="Send consented enquiries to Mailchimp"
                              />
                              {!mailchimpStatus.hasAudience ? (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                  Choose a Mailchimp audience in Apps before enabling this.
                                </Typography>
                              ) : null}
                              {values.mailchimp?.enabled ? (
                                <Field
                                  as={TextField}
                                  fullWidth
                                  name="mailchimp.consentMessage"
                                  label="Consent message (shown on the form)"
                                  variant="outlined"
                                  size="small"
                                  margin="dense"
                                  multiline
                                  minRows={3}
                                  error={fieldInputShowErr(
                                    touched.mailchimp?.consentMessage,
                                    errors.mailchimp?.consentMessage
                                  )}
                                  helperText={
                                    fieldInputShowErr(
                                      touched.mailchimp?.consentMessage,
                                      errors.mailchimp?.consentMessage
                                    ) && errors.mailchimp?.consentMessage
                                      ? errors.mailchimp.consentMessage
                                      : 'Couples see this next to a checkbox above Submit. Only ticked enquiries are synced to Mailchimp.'
                                  }
                                />
                              ) : null}
                            </>
                          ) : null}
                        </Box>
                        <Box
                          sx={{
                            p: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            overflow: 'auto',
                            flex: 1,
                            minHeight: 0
                          }}
                        >
                        <Typography variant="subtitle2" color="text.secondary">
                          Form actions
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch' }}>
                          <Button
                            variant="contained"
                            type="button"
                            disabled={isSubmitting}
                            sx={{ flex: 1 }}
                            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                            onClick={handleSaveClick}
                          >
                            {formAction === 'create' ? 'Create form' : 'Save changes'}
                          </Button>
                          {values.kind !== 'questionnaire' && (
                            <Tooltip title="Embedding settings">
                              <IconButton
                                onClick={() => setAllowedDomainsSettingsOpen(true)}
                                aria-label="Embedding settings"
                                sx={{
                                  border: 1,
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  flexShrink: 0,
                                  alignSelf: 'stretch',
                                  height: 'auto',
                                  minWidth: 48
                                }}
                              >
                                <Gear size={22} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        {values.kind === 'questionnaire' && formId && (
                          <>
                            <Button
                              variant="outlined"
                              color="secondary"
                              fullWidth
                              startIcon={<Copy size={18} />}
                              onClick={() => copyQuestionnaireShareLink(formId)}
                            >
                              Copy shareable link
                            </Button>
                            <Button
                              variant="outlined"
                              color="secondary"
                              fullWidth
                              startIcon={<PaperPlaneTilt size={18} />}
                              onClick={() => {
                                const f =
                                  forms.find((x) => String(x._id) === String(formId)) ||
                                  {
                                    ...formData,
                                    _id: formId,
                                    kind: values.kind || formData.kind || 'questionnaire'
                                  };
                                openSendQuestionnaire(f);
                              }}
                            >
                              Send email to couple
                            </Button>
                          </>
                        )}
                        {values.kind !== 'questionnaire' && (
                          <>
                            <Button
                              variant="outlined"
                              color="secondary"
                              fullWidth
                              onClick={() => {
                                if (!formId) {
                                  setError('Please save the form before getting the embed code.');
                                  return;
                                }
                                setEmbedCode(buildEmbedScriptTag(formId));
                                setShowEmbedCode(true);
                              }}
                            >
                              Embed code
                            </Button>
                            <Button
                              variant="outlined"
                              color="secondary"
                              fullWidth
                              onClick={() => openFormPreview(formId)}
                            >
                              Preview
                            </Button>
                          </>
                        )}
                        {values.kind === 'questionnaire' && !formId && (
                          <Typography variant="caption" color="text.secondary">
                            Save the questionnaire first to copy a shareable link or email a couple.
                          </Typography>
                        )}
                      </Box>
                      </>
                    )}
                  </Box>
                </Box>
              
                {values.kind !== 'questionnaire' && (
                  <Dialog
                    open={allowedDomainsSettingsOpen}
                    onClose={() => setAllowedDomainsSettingsOpen(false)}
                    maxWidth="sm"
                    fullWidth
                  >
                    <DialogTitle>Embedding settings</DialogTitle>
                    <DialogContent>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        If you would like your forms to show on your website, enter your domain below.
                      </Typography>
                      {profileWebsiteDisplay ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Your profile website ({profileWebsiteDisplay}) is kept with this form. It stays in the list below after you save.
                        </Typography>
                      ) : null}
                      <Field
                        as={TextField}
                        fullWidth
                        name="allowedDomains"
                        label="Your domains (comma separated)"
                        variant="outlined"
                        margin="normal"
                        placeholder="example.com, app.partner.com, *.example.com"
                        error={fieldInputShowErr(touched.allowedDomains, errors.allowedDomains)}
                        helperText={
                          fieldInputShowErr(touched.allowedDomains, errors.allowedDomains) &&
                          errors.allowedDomains
                        }
                      />
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                      <Button type="button" variant="contained" onClick={() => setAllowedDomainsSettingsOpen(false)}>
                        Done
                      </Button>
                    </DialogActions>
                  </Dialog>
                )}
              </Form>
              </>
            );
          }}
        </Formik>
      </Paper>
    );
  };

  return (
    <Box>
      {isEditMode && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
          mb={{ xs: 2, md: 3 }}
          sx={{ pr: { xs: 0.5, md: 0 } }}
        >
          <Box display="flex" alignItems="center" sx={{ minWidth: 0 }}>
            <IconButton
              onClick={handleBackToFormsClick}
              aria-label="Back to forms"
              edge="start"
              size="small"
              sx={{ mr: { xs: 0.25, md: 0.5 }, color: 'text.primary', flexShrink: 0 }}
            >
              <ArrowLeft size={28} />
            </IconButton>
            <Typography component="h2" className="pagename" sx={styles.text.pagename}>
              {formAction === 'create'
                ? formData.kind === 'questionnaire'
                  ? 'Create Questionnaire'
                  : 'Create Website Form'
                : formData.kind === 'questionnaire'
                  ? 'Edit Questionnaire'
                  : 'Edit Website Form'}
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              const q = formId ? `?formId=${formId}` : '';
              navigate(`/supplier/forms/submissions${q}`);
            }}
          >
            View form submissions
          </Button>
        </Box>
      )}
      
      <Box>
        {isEditMode 
          ? renderFormEditor()
          : renderForms()
        }
      </Box>

      {/* Form Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {(() => {
          const menuForm = forms.find(f => f._id === currentFormId);
          const isDefaultMenuForm = !!menuForm?.isDefault;
          return (
            <>
              <MenuItem onClick={handleEditForm}>
                <PencilSimple size={20} style={{ marginRight: 8 }} />
                Edit Form
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  openFormPreview(currentFormId);
                }}
              >
                <Eye size={20} style={{ marginRight: 8 }} />
                Preview Form
              </MenuItem>
              {menuForm?.kind !== 'questionnaire' && (
                <MenuItem onClick={handleShowEmbedCode}>
                  <ArrowSquareOut size={20} style={{ marginRight: 8 }} />
                  Embed Code
                </MenuItem>
              )}
              {menuForm?.kind === 'questionnaire' && (
                <>
                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      copyQuestionnaireShareLink(menuForm);
                    }}
                  >
                    <Copy size={20} style={{ marginRight: 8 }} />
                    Copy shareable link
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      openSendQuestionnaire(menuForm);
                    }}
                  >
                    <PaperPlaneTilt size={20} style={{ marginRight: 8 }} />
                    Send email to couple
                  </MenuItem>
                </>
              )}
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  if (menuForm) {
                    navigate(`/supplier/forms/submissions?formId=${menuForm._id}`);
                  }
                }}
              >
                <TableIcon size={20} style={{ marginRight: 8 }} />
                View in Form submissions
              </MenuItem>
              <Divider />
              {!isDefaultMenuForm && (
                <MenuItem onClick={handleDeleteForm} sx={{ color: 'error.main' }}>
                  <Trash size={20} style={{ marginRight: 8 }} />
                  Delete Form
                </MenuItem>
              )}
            </>
          );
        })()}
      </Menu>

      {/* Embed Code Dialog */}
      <Dialog
        open={showEmbedCode}
        onClose={() => setShowEmbedCode(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Embed Form on Your Website</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            Copy this code and paste it into a Custom HTML block in the page body, on its own, not inside another form. If you use a caching plugin, exclude this script so it runs on the first view. You can style it from the parent page (for example, set a wrapper class and apply fonts and colours). If you see a domain error, add your domain under Allowed Domains <em>(Forms, Edit Form, Allowed Domains)</em>.
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            value={embedCode}
            InputProps={{
              readOnly: true,
              sx: { fontFamily: 'monospace' }
            }}
            multiline
            rows={3}
          />
          {isCopied && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Code copied to clipboard!
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEmbedCode(false)}>
            Close
          </Button>
          <Button 
            variant="contained" 
            startIcon={<Copy weight="bold" />} 
            onClick={handleCopyEmbedCode}
          >
            Copy Code
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Form Preview</DialogTitle>
        <DialogContent sx={{ height: 620 }}>
          {!previewFormId ? (
            <Typography variant="body2" color="text.secondary">
              No form selected for preview.
            </Typography>
          ) : (
            <iframe
              title="Form Preview"
              style={{ border: 'none', width: '100%', height: '100%' }}
              srcDoc={previewFormData || ''}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(submissionToDelete)}
        onClose={() => {
          if (!deletingSubmission) setSubmissionToDelete(null);
        }}
      >
        <DialogTitle>Delete submission</DialogTitle>
        <DialogContent>
          <Typography>
            This removes the submission. This cannot be undone.
            {getCrmLeadIdFromEntry(submissionToDelete)
              ? ' The couple stays on your list.'
              : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubmissionToDelete(null)} disabled={deletingSubmission}>
            Cancel
          </Button>
          <Button onClick={confirmDeleteSubmission} color="error" disabled={deletingSubmission}>
            {deletingSubmission ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the form "{selectedForm?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDeleteForm} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={sendDialogOpen} onClose={() => setSendDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send questionnaire</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose a couple on your list. We will email them a private link with a button to open the questionnaire.
          </Typography>
          <LeadCoupleAutocomplete
            businessId={businessId}
            value={sendLeadId}
            onChange={setSendLeadId}
            label="Couple"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!sendLeadId} onClick={confirmSendQuestionnaire}>
            Send email
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={leaveFormDialogOpen}
        onClose={() => setLeaveFormDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Unsaved changes</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            You have unsaved changes. If you leave now, your progress will be lost.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLeaveFormDialogOpen(false)}>Stay</Button>
          <Button color="error" variant="contained" onClick={handleConfirmLeaveFormEditor}>
            Leave
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbars */}
      <PortalSnackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={() => setError('')}
      >
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      </PortalSnackbar>

      <PortalSnackbar 
        open={!!successMessage} 
        autoHideDuration={6000} 
        onClose={() => setSuccessMessage('')}
      >
        <Alert severity="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      </PortalSnackbar>
    </Box>
  );
};

export default FormLeadsManagement; 